import React, { useState, useEffect } from 'react';
import { X, Search, Package2, Plus, Check } from 'lucide-react';
import { StockItem } from '../lib/stockItems';
import { supabase } from '../lib/supabase';

interface SelectSellableItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  businessId?: string;
}

export default function SelectSellableItemsModal({
  isOpen,
  onClose,
  onSuccess,
  businessId
}: SelectSellableItemsModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [saveLoading, setSaveLoading] = useState(false);

  const loadAvailableItems = async () => {
    if (!businessId) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('stock_items')
        .select('*')
        .eq('business_id', businessId)
        .eq('is_sellable', false) // Only get items that aren't already sellable
        .order('name');

      if (error) throw error;
      setItems(data);
      setError(null);
    } catch (err) {
      console.error('Error loading available items:', err);
      setError(err instanceof Error ? err.message : 'Failed to load available items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadAvailableItems();
    }
  }, [isOpen, businessId]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
      setSelectedItems(new Set());
    }
  };

  const handleSave = async () => {
    if (selectedItems.size === 0) return;

    try {
      setSaveLoading(true);
      
      // Update all selected items to be sellable
      const { error } = await supabase
        .from('stock_items')
        .update({ is_sellable: true })
        .in('id', Array.from(selectedItems));

      if (error) throw error;

      onSuccess();
    } catch (err) {
      console.error('Error updating items:', err);
      setError(err instanceof Error ? err.message : 'Failed to update items');
    } finally {
      setSaveLoading(false);
    }
  };

  const toggleItem = (itemId: string) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const filteredItems = items.filter(item => {
    const searchLower = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower)
    );
  });

  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 bg-gray-900/50 z-[60] transition-opacity duration-300 ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white shadow-xl transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Add Sellable Items</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                  <p className="mt-4 text-sm text-gray-600">Loading items...</p>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-6">
                <div className="text-red-600 mb-2">{error}</div>
                <button
                  onClick={loadAvailableItems}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Try again
                </button>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-6">
                <Package2 className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900">
                  {searchQuery ? 'No matching items' : 'No items available'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {searchQuery ? 'Try adjusting your search terms' : 'All items are already marked as sellable'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredItems.map((item) => {
                  const isSelected = selectedItems.has(item.id);
                  return (
                    <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-sm font-medium text-gray-900">{item.name}</h3>
                          {item.description && (
                            <p className="mt-1 text-sm text-gray-500">{item.description}</p>
                          )}
                          <div className="mt-2 text-xs text-gray-500">
                            ${item.price.toFixed(2)} per {item.unit}
                          </div>
                        </div>
                        <button
                          onClick={() => toggleItem(item.id)}
                          className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                            isSelected
                              ? 'bg-green-100 text-green-800'
                              : 'text-white bg-gray-900 hover:bg-gray-800'
                          }`}
                        >
                          {isSelected ? (
                            <>
                              <Check className="h-3.5 w-3.5 mr-1.5" />
                              Selected
                            </>
                          ) : (
                            'Select'
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={selectedItems.size === 0 || saveLoading}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50"
            >
              {saveLoading ? 'Adding...' : `Add Selected Items (${selectedItems.size})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}